import React, { useState, useEffect } from "react";
import { targetsAPI } from "../utils/api";
import { Target, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Targets = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    minimum_target: "",
    set_target: "",
    stretch_target: ""
  });

  useEffect(() => {
    fetchTargets();
  }, []);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const response = await targetsAPI.getAll();
      setTargets(response.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
      toast.error("Failed to load targets");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await targetsAPI.createOrUpdate({
        year: parseInt(formData.year),
        minimum_target: parseFloat(formData.minimum_target) || 0,
        set_target: parseFloat(formData.set_target) || 0,
        stretch_target: parseFloat(formData.stretch_target) || 0
      });
      
      toast.success(`Target for ${formData.year} saved`);
      fetchTargets();
      setEditingYear(null);
      setFormData({
        year: new Date().getFullYear(),
        minimum_target: "",
        set_target: "",
        stretch_target: ""
      });
    } catch (error) {
      console.error("Error saving target:", error);
      toast.error("Failed to save target");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (target) => {
    setEditingYear(target.year);
    setFormData({
      year: target.year,
      minimum_target: target.minimum_target || "",
      set_target: target.set_target || "",
      stretch_target: target.stretch_target || ""
    });
  };

  const handleDelete = async (year) => {
    if (!window.confirm(`Delete target for ${year}?`)) return;
    
    try {
      await targetsAPI.delete(year);
      toast.success(`Target for ${year} deleted`);
      fetchTargets();
    } catch (error) {
      console.error("Error deleting target:", error);
      toast.error("Failed to delete target");
    }
  };

  const formatCurrency = (value) => {
    return `RM ${(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0 })}`;
  };

  const handleCancel = () => {
    setEditingYear(null);
    setFormData({
      year: new Date().getFullYear(),
      minimum_target: "",
      set_target: "",
      stretch_target: ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="targets-loading">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Yearly Targets</h1>
          <p className="text-slate-500 mt-1">Set revenue targets for each year</p>
        </div>
      </div>

      {/* Add/Edit Target Form */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {editingYear ? `Edit Target for ${editingYear}` : "Add New Target"}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={editingYear !== null}
                data-testid="target-year-select"
              >
                {[2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Minimum Target (RM)
              </label>
              <input
                type="number"
                value={formData.minimum_target}
                onChange={(e) => setFormData({ ...formData, minimum_target: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="minimum-target-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Set Target (RM)
              </label>
              <input
                type="number"
                value={formData.set_target}
                onChange={(e) => setFormData({ ...formData, set_target: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="set-target-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Stretch Target (RM)
              </label>
              <input
                type="number"
                value={formData.stretch_target}
                onChange={(e) => setFormData({ ...formData, stretch_target: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="stretch-target-input"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="save-target-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {editingYear ? "Update Target" : "Save Target"}
            </button>
            {editingYear && (
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Targets List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Year</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Minimum</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Set Target</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Stretch</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {targets.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-slate-500">
                  <Target size={48} className="mx-auto mb-3 text-slate-300" />
                  <p>No targets set yet</p>
                  <p className="text-sm">Add your first yearly target above</p>
                </td>
              </tr>
            ) : (
              targets.map((target) => (
                <tr key={target.year} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-semibold text-slate-900">{target.year}</td>
                  <td className="py-3 px-4 text-right text-slate-600">{formatCurrency(target.minimum_target)}</td>
                  <td className="py-3 px-4 text-right font-semibold text-primary">{formatCurrency(target.set_target)}</td>
                  <td className="py-3 px-4 text-right text-green-600">{formatCurrency(target.stretch_target)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(target)}
                        className="px-3 py-1 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        data-testid={`edit-target-${target.year}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(target.year)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-target-${target.year}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Targets;
